import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Descriptions,
  Alert,
  Tabs,
  Badge,
  Avatar
} from 'antd';
import {
  CheckOutlined,
  EyeOutlined,
  UserOutlined,
  SoundOutlined,
  ReloadOutlined,
  WechatOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const schoolTypeLabels = {
  kindergarten: '幼儿园',
  primary: '小学',
  middle: '初中',
  high: '高中',
  nine_year: '九年一贯制',
  twelve_year: '十二年一贯制',
  vocational: '中职/职校',
  other: '其他'
};

const readRegistrationMetadata = (record = {}) => {
  const raw = record.metadata;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const schoolRegionText = (meta = {}) => {
  return [meta.schoolProvince, meta.schoolCity, meta.schoolCounty].filter(Boolean).join(' ');
};

const UserRegistrations = ({
  fixedRegistrationType = '',
  title = '用户注册管理',
  showTypeFilter = true
}) => {
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  
  // 模态框状态
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  
  // 表单和过滤状态
  const [reviewForm] = Form.useForm();
  const [filters, setFilters] = useState({
    status: 'pending',
    registration_type: fixedRegistrationType
  });

  // 获取注册申请列表
  const fetchRegistrations = async (page = 1, pageSize = 10, filterParams = {}) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...filterParams
      });
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/auth/registrations?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        const total = data.data?.pagination?.total ?? data.data?.total ?? 0;
        setRegistrations(data.data.registrations);
        setPagination({
          current: page,
          pageSize: pageSize,
          total
        });
      } else {
        message.error(data.message || '获取注册申请失败');
      }
    } catch (error) {
      console.error('获取注册申请失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    fetchRegistrations(1, 10, {
      ...filters,
      registration_type: fixedRegistrationType || filters.registration_type
    });
  }, [filters, fixedRegistrationType]);

  // 审核注册申请
  const handleReview = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/auth/registrations/${currentRecord.id}/review`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(values)
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success(data.message);
        setReviewModalVisible(false);
        reviewForm.resetFields();
        setCurrentRecord(null);
        // 刷新列表
        fetchRegistrations(pagination.current, pagination.pageSize, {
          ...filters,
          registration_type: fixedRegistrationType || filters.registration_type
        });
      } else {
        message.error(data.message || '审核失败');
      }
    } catch (error) {
      console.error('审核失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '申请类型',
      key: 'registration_info',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Tag 
            icon={record.registration_type === 'school_admin' ? <SoundOutlined /> : <UserOutlined />}
            color={record.registration_type === 'school_admin' ? 'blue' : 'green'}
          >
            {record.registration_type === 'school_admin' ? '学校管理员' : '教师用户'}
          </Tag>
          {record.wechat_openid && (
            <Tag icon={<WechatOutlined />} color="#07c160" style={{ fontSize: '12px' }}>
              微信登录
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: '用户信息',
      key: 'userInfo',
      width: 200,
      render: (_, record) => (
        <Space>
          <Avatar 
            src={record.wechat_avatar} 
            icon={<UserOutlined />}
            size="default"
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {record.wechat_nickname || record.name || record.username}
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              @{record.username}
              {record.wechat_openid && (
                <WechatOutlined style={{ marginLeft: 4, color: '#07c160' }} />
              )}
            </div>
          </div>
        </Space>
      )
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 180
    },
    {
      title: '学校名称',
      dataIndex: 'school_name',
      key: 'school_name',
      width: 200,
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.registration_type === 'school_admin' && (() => {
            const meta = readRegistrationMetadata(record);
            const region = schoolRegionText(meta);
            return region ? (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {region}
              </div>
            ) : null;
          })()}
          {record.matched_school_name && (
            <div style={{ fontSize: '12px', color: '#52c41a' }}>
              ✓ 已匹配：{record.matched_school_name}
            </div>
          )}
        </div>
      )
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 120
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusConfig = {
          pending: { color: 'orange', text: '待审核' },
          approved: { color: 'green', text: '已通过' },
          rejected: { color: 'red', text: '已拒绝' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (time) => new Date(time).toLocaleDateString()
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setCurrentRecord(record);
              setDetailModalVisible(true);
            }}
          >
            详情
          </Button>
          {record.status === 'pending' && (
            <Button
              type="link"
              size="small"
              icon={<CheckOutlined />}
              style={{ color: '#52c41a' }}
              onClick={() => {
                setCurrentRecord(record);
                reviewForm.setFieldsValue({ action: 'approve' });
                setReviewModalVisible(true);
              }}
            >
              审核
            </Button>
          )}
        </Space>
      )
    }
  ];

  // 获取统计数据
  const getStatusCounts = () => {
    const counts = {
      pending: 0,
      approved: 0,
      rejected: 0,
      total: registrations.length
    };
    
    registrations.forEach(reg => {
      if (counts.hasOwnProperty(reg.status)) {
        counts[reg.status]++;
      }
    });
    
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{title}</span>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => fetchRegistrations(pagination.current, pagination.pageSize, {
                ...filters,
                registration_type: fixedRegistrationType || filters.registration_type
              })}
            >
              刷新
            </Button>
          </div>
        }
      >
        {/* 状态统计标签页 */}
        <Tabs
          activeKey={filters.status}
          onChange={(key) => setFilters({ ...filters, status: key })}
          items={[
            {
              key: 'pending',
              label: (
                <Badge count={statusCounts.pending} offset={[10, 0]}>
                  <span>待审核</span>
                </Badge>
              )
            },
            {
              key: 'approved',
              label: (
                <Badge count={statusCounts.approved} offset={[10, 0]}>
                  <span>已通过</span>
                </Badge>
              )
            },
            {
              key: 'rejected',
              label: (
                <Badge count={statusCounts.rejected} offset={[10, 0]}>
                  <span>已拒绝</span>
                </Badge>
              )
            },
            {
              key: '',
              label: (
                <Badge count={statusCounts.total} offset={[10, 0]}>
                  <span>全部</span>
                </Badge>
              )
            }
          ]}
        />

        {/* 过滤器 */}
        {showTypeFilter && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
            <Select
              placeholder="选择申请类型"
              style={{ width: 150 }}
              value={filters.registration_type}
              onChange={(value) => setFilters({ ...filters, registration_type: value })}
              allowClear
            >
              <Option value="school_admin">学校管理员</Option>
              <Option value="teacher">教师用户</Option>
            </Select>
          </div>
        )}

        {/* 申请列表表格 */}
        <Table
          columns={columns}
          dataSource={registrations}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              fetchRegistrations(page, pageSize, filters);
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 审核模态框 */}
      <Modal
        title="审核注册申请"
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          reviewForm.resetFields();
          setCurrentRecord(null);
        }}
        footer={null}
        width={600}
      >
        {currentRecord && (
          <Form
            form={reviewForm}
            onFinish={handleReview}
            layout="vertical"
            initialValues={{ action: 'approve' }}
          >
            <Alert
              message="申请信息"
              description={
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="申请类型">
                    <Tag color={currentRecord.registration_type === 'school_admin' ? 'blue' : 'green'}>
                      {currentRecord.registration_type === 'school_admin' ? '学校管理员' : '教师用户'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="用户名">{currentRecord.username}</Descriptions.Item>
                  <Descriptions.Item label="邮箱">{currentRecord.email}</Descriptions.Item>
                  <Descriptions.Item label="学校名称">{currentRecord.school_name}</Descriptions.Item>
                  {currentRecord.phone && (
                    <Descriptions.Item label="联系电话">{currentRecord.phone}</Descriptions.Item>
                  )}
                </Descriptions>
              }
              type="info"
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="action"
              label="审核决定"
              rules={[{ required: true, message: '请选择审核结果' }]}
            >
              <Select>
                <Option value="approve">通过申请</Option>
                <Option value="reject">拒绝申请</Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.action !== currentValues.action
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('action') === 'reject' && (
                  <Form.Item
                    name="rejection_reason"
                    label="拒绝原因"
                    rules={[{ required: true, message: '请输入拒绝原因' }]}
                  >
                    <TextArea
                      rows={3}
                      placeholder="请说明拒绝申请的原因"
                    />
                  </Form.Item>
                )
              }
            </Form.Item>

            <Form.Item
              name="admin_notes"
              label="管理员备注（可选）"
            >
              <TextArea
                rows={2}
                placeholder="可添加审核备注信息"
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  确认审核
                </Button>
                <Button onClick={() => setReviewModalVisible(false)}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="申请详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setCurrentRecord(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {currentRecord && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="申请类型" span={2}>
              <Tag 
                icon={currentRecord.registration_type === 'school_admin' ? <SoundOutlined /> : <UserOutlined />}
                color={currentRecord.registration_type === 'school_admin' ? 'blue' : 'green'}
              >
                {currentRecord.registration_type === 'school_admin' ? '学校管理员' : '教师用户'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="用户名">{currentRecord.username}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{currentRecord.email}</Descriptions.Item>
            <Descriptions.Item label="学校名称">{currentRecord.school_name}</Descriptions.Item>
            <Descriptions.Item label="联系电话">{currentRecord.phone || '未填写'}</Descriptions.Item>
            
            {/* 学校管理员专用字段 */}
            {currentRecord.registration_type === 'school_admin' && (() => {
              const meta = readRegistrationMetadata(currentRecord);
              return (
                <>
                  <Descriptions.Item label="学校地区" span={2}>
                    {schoolRegionText(meta) || '未填写'}
                  </Descriptions.Item>
                  <Descriptions.Item label="学校类型">
                    {schoolTypeLabels[meta.schoolType] || meta.schoolType || '未填写'}
                  </Descriptions.Item>
                  <Descriptions.Item label="学校标识码">
                    {meta.schoolCode || '未填写'}
                  </Descriptions.Item>
                  <Descriptions.Item label="详细地址" span={2}>
                    {meta.schoolAddress || '未填写'}
                  </Descriptions.Item>
                  <Descriptions.Item label="联系人">
                    {meta.contactName || meta.adminName || currentRecord.display_name || currentRecord.name || '未填写'}
                  </Descriptions.Item>
                  <Descriptions.Item label="联系手机号">
                    {meta.contactPhone || meta.adminPhone || currentRecord.phone || '未填写'}
                  </Descriptions.Item>
                  <Descriptions.Item label="申请说明" span={2}>
                    {meta.applicationNote || '未填写'}
                  </Descriptions.Item>
                </>
              );
            })()}
            
            {/* 教师专用字段 */}
            {currentRecord.registration_type === 'teacher' && (
              <Descriptions.Item label="任教科目">{readRegistrationMetadata(currentRecord).subject || currentRecord.subject || '未填写'}</Descriptions.Item>
            )}
            
            <Descriptions.Item label="申请状态">
              <Tag color={
                currentRecord.status === 'pending' ? 'orange' :
                currentRecord.status === 'approved' ? 'green' : 'red'
              }>
                {currentRecord.status === 'pending' ? '待审核' :
                 currentRecord.status === 'approved' ? '已通过' : '已拒绝'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="申请时间">
              {new Date(currentRecord.created_at).toLocaleString()}
            </Descriptions.Item>
            
            {currentRecord.approved_at && (
              <>
                <Descriptions.Item label="审核时间">
                  {new Date(currentRecord.approved_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="审核人">
                  {currentRecord.approved_by_username || '系统'}
                </Descriptions.Item>
              </>
            )}
            
            {currentRecord.rejection_reason && (
              <Descriptions.Item label="拒绝原因" span={2}>
                {currentRecord.rejection_reason}
              </Descriptions.Item>
            )}
            
            {currentRecord.admin_notes && (
              <Descriptions.Item label="管理员备注" span={2}>
                {currentRecord.admin_notes}
              </Descriptions.Item>
            )}
            
            {currentRecord.matched_school_name && (
              <Descriptions.Item label="匹配学校" span={2}>
                <span style={{ color: '#52c41a' }}>
                  ✓ {currentRecord.matched_school_name}
                </span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default UserRegistrations;
